import React, { createContext, Fragment, useContext, useEffect, useState } from "react"
import { Copy } from "lucide-react"
import { useAppCtx, appStore } from "lib/state"
import { Menubar, Tabset } from "lib/components"
import { queryTools, cn, inspect } from "lib/utils"
import { ApiDatapoint, ApiDatapointEditor, useApiData } from "lib/units/apidata"

const { apiDataTypes, getDataType } = inspect
const buttonClasses = "w-6/12 bg-transparent text-sm text-neutral-300 border border-solid border-slate-700 px-2 py-1"

export default function ApiDataManager({ 
  format="standard", 
  selectItemCallbackFn, 
  closeFn, 
  className="" 
}:{ 
  format?:string
  selectItemCallbackFn:any
  closeFn:any 
  className?:string
}){

  const { getItemsBySegment, selectItem}:any = useApiData()

  const { store, AppStore }:any = appStore()
  const { update }:any = AppStore

  const [ currentOrigin,  setCurrentOrigin ] = useState<string>("rp")
  const [ searchData, setSearchData ] = useState<string>("") 
  const [ searchParams, setSearchParams ] = useState<any>({ path: "", segment: "" })
  const [ searchResults, setSearchResults ] = useState<any[]>()

  const { data:_ApiData } = queryTools.resource.parseQueryForApi("apidata.findAll", {})

  const searchApiData = (e:any) => {
    setSearchData(e.target.value)
    if( e.target.value.length <= 2 ) return setSearchResults(_ApiData || [])
    const result:any[] = _ApiData?.filter( (data:any) => 
      data.label?.length > 0 && ( 
        data.label?.toLowerCase().startsWith(e.target.value.toLowerCase()) 
        || data.placeholder?.toLowerCase().startsWith(e.target.value.toLowerCase())
        || data.description?.toLowerCase().includes(e.target.value.toLowerCase()) 
      ) 
    )
    setSearchResults(result)
  }

  const menubar = [
    { 
      refstr: "display", 
      label: "Filter",
      submenus: [
        { type: "option", refstr: "rp", label: "Representment" },
        { type: "option", refstr: "rp-all", label: "All", selectFn: () => setSearchResults(getItemsBySegment({path: "rp"})) },
        { type: "option", refstr: "rp-addtrx", label: "Addtl Trx", selectFn: () => setSearchResults(getItemsBySegment({path: "rp", segment: "AdditionalTransactions"})) },
        { type: "option", refstr: "rp-cb", label: "Chargeback", selectFn: () => setSearchResults(getItemsBySegment({path: "rp", segment: "Chargeback"}) )},
        { type: "option", refstr: "rp-docs", label: "Documents", selectFn: () => setSearchResults(getItemsBySegment({path: "rp", segment: "Documents"})) },
        { type: "option", refstr: "rp-orders", label: "Order Info", selectFn: () => setSearchResults(getItemsBySegment({path: "rp", segment: "OrderInfo"})) },
        { type: "divider", refstr: "divider1", label: "---" },
        { type: "option", refstr: "merch", label: "Merchant" },
        { type: "option", refstr: "merch-all", label: "All", selectFn: () => setSearchResults(getItemsBySegment({path: "merch", segment: ""})) },
      ]
    },
  ]

  useEffect(() => {
    if( _ApiData ) setSearchResults(getItemsBySegment(searchParams))
  },[_ApiData, searchParams])

  if( !_ApiData || !searchResults ) return <></>
  return (
    <div 
      className={cn(
        "w-full h-full flex pb-2 flex gap-5",
        format === "compact" ? "flex-col pb-0" : "flex-row",
        className
      )}
    >
      <div className={cn(
        "w-full overflow-y-hidden flex",
        format === "standard" ? "w-full h-[36rem] flex-row" : "w-full flex-col h-[calc(100%-2rem)]"
      )}>
        
        <div className="bg-slate-900 py-5 pl-[2px] pr-[4px] overflow-y-auto scrollbar">
          <div className="w-full px-2 flex items-center gap-3 border border-solid border-slate-700">
            <div className="w-4/12">
              <Menubar className="w-full h-full flex justify-center items-center text-xs pt-1" menus={menubar} />
            </div>
            <input 
              type="text" 
              className="w-8/12 h-8 px-3 bg-transparent border-l border-solid border-slate-700 text-sm text-neutral-300" 
              placeholder="Search API..." 
              value={searchData}
              onChange={searchApiData}
            />
          </div>

          {/* <div className="w-full h-6 flex justify-start items-center text-sm text-neutral-300 px-[2px] my-2 border border-solid border-slate-700">
            <button 
              className={cn(
                "w-full flex justify-center items-center text-sm divide-x divide-slate-700 divide",
                currentOrigin === "rp" ? "bg-slate-700 text-amber-300" : "text-neutral-300" 
              )}
              onClick={() => setCurrentOrigin("rp")}
            >
              rp 
            </button>
            <button 
              className={cn(
                "w-full flex justify-center items-center text-sm",
                currentOrigin === "merch" ? "bg-slate-700 text-amber-300" : "text-neutral-300"
              )}
              onClick={() => setCurrentOrigin("merch")}
            >
              merch 
            </button>
          </div> */}
          
          <div className="w-full py-2 flex flex-col gap-[3px]">
            { searchResults?.map( (item:any) => (
              <span 
                key={item._id} 
                className={cn(
                  "w-full border border-solid border-transparent pl-4 pr-3 py-[3px] hover:border-teal-700/50 cursor-pointer",
                  store.actives.apidata?._id === item._id ? "border-teal-700/50 bg-teal-800" : "border-transparent"
                )}
                onClick={() => update("actives.apidata", item)}
              >
                {item.label}
              </span>
            ))}
          </div>

          {/* <div className="w-full px-3 py-2">
            { getDirectChildren(currentOrigin, searchResults).map( (item:any) => (
              <ApiDatapoint key={item._id} obj={item} level={1} />
            ))}
          </div> */}
        </div>

        { format === "standard" && (
          <div className="w-7/12 h-[36rem]">
            { store.actives.apidata ? (
              <ApiDatapointEditor onItemSelect={selectItemCallbackFn} closeFn={closeFn} />
            ) : (
              <div className="w-full h-full flex justify-center items-center text-base text-white">
                <p>Choose an item from the API tree at left.</p>
              </div>
            )}
          </div>
        )}
      </div>

      { format === "compact" && (
        <div className="w-full h-[2rem] flex justify-center items-center px-2 pb-4">
          <button className={cn(buttonClasses, "w-full bg-slate-700")} onClick={() => selectItemCallbackFn(store.actives.apidata)}>
            Select
          </button>
        </div>
      )}
    </div>
  )
}